import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {ColDef, GridOptions, RowClassParams} from 'ag-grid-community';
import {Observable} from 'rxjs';
import {IStore} from '../../../shared-models/store/store';
import {ModalControllerService} from '../../../shared-services/overlay/modal-controller.service';
import {GridUtils} from '../../../../shared-utilities/utils-old/grid-utils-old/grid-utils';
import {addQuoteItemsArray} from '../../../../features/quotes/store/quotes.actions';
import * as StockSearchActions from '../../../../shared-modules/shared-process-grid/store/stock-search.actions';
import {
  selectStockSearchAdvancedData,
} from '../../../../shared-modules/shared-process-grid/store/stock-search.selectors';
import {IStockSearchAdvancedData} from '../../../../shared-modules/shared-process-grid/store/stock-search.models';
import {getDeepCopyOfObject} from '../../../shared-utils/object/object.utils';
import {calculateQuoteNominalGp} from '../../../../features/quotes/utils/quote-gp.utils';
import {getQuoteStockItemVatRate} from '../../../../features/quotes/utils/quote-vat.utils';
import {
  getVatRateConversionWithoutExistingVatRates,
} from '../../../../shared-modules/shared-grid/store/shared-grid.actions';
import {selectStores} from '../../../../features/user-access/user-stores-store/user-store.selectors';
import {
  selectSharedGridVatRateBySelectedStore,
} from '../../../../shared-modules/shared-grid/store/shared-grid.selectors';
import {take} from 'rxjs/operators';
import {IQuoteStockItem} from '../../../../features/quotes/models/quoteStockItems';
import {StockItem} from '../../../../shared-utilities/models-old/datastructures';

/**
 * Wrapper component for shared-stock-search that dispatches addQuoteItem for selected items.
 * Used in quotes feature to allow advanced stock item search.
 */
export const QUOTE_STOCK_SEARCH_MODAL_CSS = 'quote-stock-search-modal-css';

@Component({
  selector: 'app-quote-stock-search-wrapper',
  templateUrl: './quote-stock-search-wrapper.component.html',
  styleUrls: ['./quote-stock-search-wrapper.component.scss'],
  standalone: false,
})
export class QuoteStockSearchWrapperComponent implements OnInit, OnDestroy {
  /**
   * Store context for filtering stock items.
   */
  @Input() currentStore: IStore | null = null;

  /**
   * Grid column definitions for stock items.
   */
  gridColDefs: ColDef[] = [
    {
      field: 'code',
      headerName: 'Code',
      hide: false,
      pinned: 'left',
      resizable: true,
      lockPinned: true,
      lockPosition: true,
      editable: false,
      width: 200,
      cellEditor: false,
      suppressHeaderMenuButton: true,
    },
    {
      field: 'desc',
      headerName: 'Description',
      hide: false,
      lockPinned: true,
      resizable: true,
      editable: false,
      cellEditor: false,
      suppressHeaderMenuButton: true,
    },
    {
      field: 'suppCode',
      headerName: 'Supplier Code',
      hide: false,
      lockPinned: true,
      resizable: false,
      editable: false,
      cellEditor: false,
      suppressHeaderMenuButton: true,
    },
  ];

  /**
   * Grid options configuration.
   */
  gridOptions: GridOptions = {
    colResizeDefault: 'shift',
    debug: false,
    editType: null,
    enterNavigatesVertically: true,
    enterNavigatesVerticallyAfterEdit: true,
    suppressAnimationFrame: true,
    enableCellTextSelection: false,
    multiSortKey: 'ctrl',
    rowBuffer: 100,
    pagination: true,
    rowHeight: 45,
    suppressScrollOnNewData: true,
    paginationAutoPageSize: false,
    paginationPageSize: 50,
    paginationPageSizeSelector: [10, 15, 20, 50, 100],
    suppressPaginationPanel: true,
    rowDragManaged: false,
    rowDragEntireRow: false,
    rowDragMultiRow: false,
    rowSelection: {
      mode: 'multiRow',
      checkboxes: false,
      enableClickSelection: true,
      enableSelectionWithoutKeys: true,
      headerCheckbox: false,
      isRowSelectable: (node) => !!node.data,
    },
    singleClickEdit: true,
    stopEditingWhenCellsLoseFocus: true,
    undoRedoCellEditing: false,
    undoRedoCellEditingLimit: 25,
    getRowStyle: (params: RowClassParams): Record<string, string> => ({
      backgroundColor: `${params.data.lineColourValue}`,
      color: `${params.data.lineColourValue ? GridUtils.getContrastColor(params.data.lineColourValue as string) : 'initial'}`,
      fill: `${params.data.lineColourValue ? 'black' : 'var(--gallix-color-white-contrast)'}`,
    }),
  };

  /**
   * Observable for stock search data with pagination.
   */
  stockSearchData$: Observable<IStockSearchAdvancedData | null>;

  constructor(
    private readonly store: Store,
    private readonly modalController: ModalControllerService,
  ) {
  }

  ngOnInit(): void {
    this.store.select(selectStores).pipe(take(1)).subscribe((userStores) => {
      this.store.dispatch(getVatRateConversionWithoutExistingVatRates({userStores: userStores ?? []}));
    });

    if (this.currentStore) {
      this.store.dispatch(StockSearchActions.searchStockItemsAdvanced({
        query: '',
        store: this.currentStore,
        filters: {},
      }));
    }

    this.stockSearchData$ = this.store.select(selectStockSearchAdvancedData);
  }

  ngOnDestroy(): void {
    this.store.dispatch(StockSearchActions.clearStockSearch());
  }

  /**
   * Handles selected stock items by dispatching addQuoteItem for each item.
   * Calculates nominalGP for each item before adding.
   * Closes modal after dispatching actions.
   */
  handleSelectedItems(stockItemsArray: StockItem[]): void {
    const quoteItems = getDeepCopyOfObject(stockItemsArray);

    this.store.select(selectSharedGridVatRateBySelectedStore).pipe(take(1)).subscribe((vatRates) => {
      const itemsWithGP = quoteItems.map((item: IQuoteStockItem) => {
        const baseItem = {
          ...item,
          orderQty: 1,
          originalSellPriIncl1: Number(item.sellPriIncl1 ?? 0),
        };

        if (!this.currentStore || getQuoteStockItemVatRate(item, this.currentStore, vatRates) === undefined) {
          return baseItem;
        }

        const nominalGp: number | null = calculateQuoteNominalGp(item, this.currentStore, vatRates);

        return {
          ...item,
          orderQty: 1,
          originalSellPriIncl1: Number(item.sellPriIncl1 ?? 0),
          nominalGP: nominalGp ?? item.nominalGP,
        };
      });

      this.store.dispatch(addQuoteItemsArray({items: itemsWithGP}));
      void this.modalController.dismiss();
    });
  }
}
